import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Typography } from '@mui/material';

import { Dialog, dialogClasses } from '@/components/Dialog';
import { RadioGroupList } from '@/components/RadioGroupList';
import { PLAN_TYPE } from '@/const/plans';
import { subscribe } from '@/store/billing/thunks';
import { Button } from '@/components/Button';
import {
	useShallowEqualSelector,
	useSubscribed,
	useSubscriptionInfo
} from '@/hooks';
import { usePlanOptions } from '@/hooks/price';
import { showError, showSuccess } from '@/utils';
import { RESULT_CODES } from '@/components/payment/adyen/constants';
import { usePlan } from '@/pages/private/checkout/hooks';

import { useHandleRetry } from '../../hooks';

export function ManageSubscriptionModal({ open, onClose }) {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const handleRetry = useHandleRetry();
	const subscribed = useSubscribed();

	const { mainPlan = null } = usePlan();

	const [saveLoading, setSaveLoading] = useState(false);

	const { prices = [], fetchPrices } = useShallowEqualSelector(
		state => state.checkout
	);

	const { loading = false } = fetchPrices || {};

	const {
		handlerPriceId,
		cancellationRequested,
		isCanceled,
		canUseTrial,
		isTrial,
		plan: currentPlan
	} = useSubscriptionInfo();

	const [selected, setSelected] = useState(null);

	const currentPlanId = prices.find(p => p.name === currentPlan)?.handlerId;

	const willCancel = cancellationRequested && !isCanceled;

	const filterTrial = !canUseTrial && !(isTrial && willCancel);

	const plans = useMemo(() => {
		if (!prices?.length) {
			return [];
		}

		return prices.filter(
			price => filterTrial && price.name !== PLAN_TYPE.TRIAL
		);
	}, [prices, filterTrial]);

	const planOptions = usePlanOptions(plans, { withPeriod: true });

	async function switchPlan({ priceId, mainPriceId }) {
		try {
			const { handlerData } = await dispatch(
				subscribe({
					priceId,
					mainPriceId
				})
			).unwrap();

			if (handlerData?.resultCode !== RESULT_CODES.AUTHORIZED) {
				handleRetry({
					priceId
				});
			}
		} catch (error) {
			showError(error);
		}
	}

	async function handleChangePlan() {
		try {
			setSaveLoading(true);

			const mustSwitchPlan = selected !== handlerPriceId;

			if (cancellationRequested && isCanceled) {
				handleRetry({
					priceId: handlerPriceId
				});
			} else if (mustSwitchPlan) {
				await switchPlan({
					priceId: selected,
					mainPriceId: mainPlan?.handlerId
				});
			}

			showSuccess(t('billing.switchSubscriptionSuccess'));
			onClose();
		} catch (error) {
			showError(error);
		} finally {
			setSaveLoading(false);
			onClose?.();
		}
	}

	const parsedPrices = useMemo(
		() =>
			filterTrial
				? prices.filter(item => item.name !== PLAN_TYPE.TRIAL)
				: prices,
		[prices, filterTrial]
	);

	useEffect(() => {
		if (currentPlan !== PLAN_TYPE.TRIAL || !filterTrial) {
			return;
		}

		const next = parsedPrices?.[0]?.handlerId || currentPlanId;

		setSelected(prev => (prev === next ? prev : next));
	}, [currentPlan, filterTrial, parsedPrices, currentPlanId]);

	return (
		<Dialog
			open={open}
			sx={{
				[`.${dialogClasses.paper}`]: {
					width: '100%',
					py: { xs: 2.5, sm: 3.5 },
					px: { xs: 2, sm: 3.5 }
				}
			}}
			maxWidth="sm"
			onClose={onClose}
		>
			<Box
				sx={theme => ({
					display: 'flex',
					alignItems: 'center',
					gap: 3,
					[theme.breakpoints.down('sm')]: {
						gap: 1.5,
						flexDirection: 'column',
						alignItems: 'flex-start'
					}
				})}
			>
				<Typography
					fontWeight="bold"
					sx={{
						fontSize: { xs: 16, sm: 20 }
					}}
				>
					{subscribed
						? t('billing.changePlan')
						: t('billing.manageSubscription')}
				</Typography>
			</Box>
			<Box>
				{loading && !prices?.length ? (
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							pt: 5,
							minHeight: 140
						}}
					>
						<CircularProgress size={50} />
					</Box>
				) : (
					<Box>
						<Box sx={{ mt: 3 }}>
							<RadioGroupList
								name="plan"
								value={selected}
								onChange={event => setSelected(event.target.value)}
								showSeeMore={false}
								options={planOptions}
							/>
						</Box>
					</Box>
				)}
				<Box
					sx={{
						display: 'flex',
						gap: 2,
						justifyContent: 'space-between',
						pt: 2,
						mt: 3,
						borderTop: 1,
						borderColor: 'divider',
						'.MuiButton-root': {
							minWidth: { xs: 92, sm: 147 }
						}
					}}
				>
					<Button variant="outlined" onClick={onClose}>
						{t('common.cancel')}
					</Button>
					<Button
						loading={saveLoading}
						variant="contained"
						disabled={!selected}
						onClick={handleChangePlan}
					>
						{subscribed ? t('common.save') : t('common.subscribe')}
					</Button>
				</Box>
			</Box>
		</Dialog>
	);
}
